import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Box, Card, Divider, Paper, Tooltip } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { format, addDays } from "date-fns";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzDaySelectButton from "src/components/core-components/mui/ByzDaySelect";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import { GridColDef, GridSortModel } from "@mui/x-data-grid-premium";
import { useTranslate } from "src/locales";
import SimpleSelectListPopup from "src/components/core-components/mui/inputs/SimpleSelectListPopup";
import { useSelector } from "react-redux";
import { RootState } from "src/app/store";
import ByzMuiSearchFilterButton from "src/components/core-components/mui/buttons/ByzMuiSearchFilterButton";
import { surgeryService } from "src/api/surgery/surgeryService";
import { useSnackbar } from "notistack";
import ByzMuiOpenProcessMenu from "src/components/core-components/mui/ByzMuiOpenProcessMenu";
import ByzMuiPrintButton from "src/components/core-components/mui/buttons/ByzMuiPrintButton";
import { dateTimeFormatter } from "src/utils/date";

interface PatientListRow {
  id: string;
  patientName: string;
  patientSurname: string;
  lastStatus: string;
  fileNo: string;
  protocolNo: string;
  department: string;
  doctorName: string;
  processName: string;
  ptid: number;
  ppid: number;
  upn: number;
  psid?: number;
  lastpsid?: number;
}

interface ExaminationStatusRow {
  id: string;
  actionUser: string;
  actionTime: string;
  status: string;
  changedBy: string;
  changeTime: string;
  psid?: number;
  ptid?: number;
  ppid?: number;
  upn?: number;
}

type RowInfoOverride = {
  ptid: number;
  ppid: number;
  upn: number;
};

interface NurseExaminationProps {
  patientId?: number | null;
}

const NurseExamination: React.FC<NurseExaminationProps> = ({ patientId: propPatientId }) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [building, setBuilding] = useState<any>(null);
  const [floor, setFloor] = useState<any>(null);
  const [department, setDepartment] = useState<any>(null);
  const [bed, setBed] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [patientListRows, setPatientListRows] = useState<PatientListRow[]>([]);
  const [examinationStatusRows, setExaminationStatusRows] = useState<ExaminationStatusRow[]>([]);
  const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<PatientListRow | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [rowInfoMap, setRowInfoMap] = useState<Record<string, RowInfoOverride>>({});
  const selectedOfficeId = useSelector((state: RootState) => state.auth.selectedOfficeId);

  const mainTabs = useSelector((state: any) => state.tabs.mainTabs);
  const activeTabIndex = useSelector((state: any) => state.tabs.activeTabIndex);
  const activeTab = mainTabs[activeTabIndex];
  const tabPatientId = activeTab?.data?.patientId;
  const currentPatientId: number = propPatientId || tabPatientId || 0;

  const handleBuildingChange = useCallback((value: any) => {
    setBuilding(value);
    setFloor(null);
    setDepartment(null);
    setBed(null);
  }, []);

  const handleFloorChange = useCallback((value: any) => {
    setFloor(value);
    setBed(null);
  }, []);

  const handleSearch = useCallback(async () => {
    try {
      setIsLoading(true);

      setExaminationStatusRows([]);
      setSelectedRow(null);
      setSelectedRowId(null);

      const requestBody = {
        ExaminationDate: selectedDate ? selectedDate.toISOString() : new Date().toISOString(),
        PtId: currentPatientId,
        HospitalBuildingId: building?.ID ?? building?.id ?? null,
        HospitalFloorId: floor?.ID ?? floor?.id ?? null,
        DepartmentId: department?.ID ?? department?.id ?? null,
      };

      const response = await surgeryService.examinationProcess.getExaminationProcessSummary(requestBody);

      if (response.issuccess && response.data) {
        // Response'dan gelen verileri PatientListRow formatına map et
        const mappedRows: PatientListRow[] = response.data.map((item: any) => ({
          id: `patient_${item.ptid}_${item.ppid}`,
          patientName: item.name || "",
          patientSurname: item.surname || "",
          lastStatus: item.processstatusname || "",
          fileNo: item.upn?.toString() || "",
          protocolNo: item.protid?.toString() || "",
          department: item.departmentname || "",
          doctorName: item.doctorname || "",
          processName: item.processname || "",
          ptid: item.ptid || 0,
          ppid: item.ppid || 0,
          upn: item.upn || 0,
          psid: item.psid || item.psid2 || undefined,
          lastpsid: item.lastpsid || undefined,
        }));

        setPatientListRows(mappedRows);
      } else {
        enqueueSnackbar(response.message || t("nurse-examination.messages.search-error"), {
          variant: "error",
          autoHideDuration: 5000,
        });
      }
    } catch (error) {
      console.error("Error fetching examination process summary:", error);
      enqueueSnackbar(t("nurse-examination.messages.search-error"), {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate, currentPatientId, building, floor, department, t, enqueueSnackbar]);

  const fetchExaminationStatus = useCallback(
    async (row: PatientListRow): Promise<RowInfoOverride | null> => {
      try {
        setIsLoadingStatus(true);
        setExaminationStatusRows([]);

        const requestBody = {
          PtId: row.ptid,
          ExaminationDate: selectedDate ? selectedDate.toISOString() : new Date().toISOString(),
          PpId: row.ppid,
        };

        const response = await surgeryService.examinationProcess.getExaminationStatus(requestBody);

        if (response.issuccess && response.data) {
          const sortedData = [...response.data].sort((a: any, b: any) => (b.id || 0) - (a.id || 0));

          const mappedRows: ExaminationStatusRow[] = sortedData.map((item: any, index: number) => ({
            id: `${item.id}`,
            actionUser: item.cuser || "",
            actionTime: item.ctime || "",
            status: item.status || "",
            changedBy: item.muser || "",
            changeTime: item.mtime || "",
            psid: item.psid || item.psid2 || undefined,
            ptid: item.ptid || row.ptid,
            ppid: item.ppid || row.ppid,
            upn: typeof item.upn === "number" ? item.upn : undefined,
          }));

          setExaminationStatusRows(mappedRows);

          const latestStatus = mappedRows.length > 0 ? mappedRows[0] : null;
          if (latestStatus) {
            if (typeof latestStatus.upn === "number") {
              const override: RowInfoOverride = {
                ptid: latestStatus.ptid || row.ptid,
                ppid: latestStatus.ppid || row.ppid,
                upn: latestStatus.upn,
              };
              setRowInfoMap((prev) => ({
                ...prev,
                [row.id]: override,
              }));
              setSelectedRow((prevRow) =>
                prevRow && prevRow.ptid === row.ptid && prevRow.ppid === row.ppid
                  ? {
                    ...prevRow,
                    psid: latestStatus.psid || prevRow.psid,
                    ptid: override.ptid,
                    ppid: override.ppid,
                    upn: override.upn,
                  }
                  : prevRow
              );
              return override;
            }
          }
        } else {
          enqueueSnackbar(response.message || t("nurse-examination.messages.status-error"), {
            variant: "error",
            autoHideDuration: 5000,
          });
        }
      } catch (error) {
        console.error("Error fetching examination status:", error);
        enqueueSnackbar(t("nurse-examination.messages.status-error"), {
          variant: "error",
          autoHideDuration: 5000,
        });
        return null;
      } finally {
        setIsLoadingStatus(false);
      }
      return null;
    },
    [selectedDate, t, enqueueSnackbar]
  );

  const handleSendToExamination = useCallback(async (row: PatientListRow) => {
    try {
      let overrides: RowInfoOverride | undefined = rowInfoMap[row.id];
      if (!overrides) {
        overrides = (await fetchExaminationStatus(row)) || undefined;
      }
      if (!overrides) {
        enqueueSnackbar(t("nurse-examination.messages.status-error"), {
          variant: "error",
          autoHideDuration: 5000,
        });
        return;
      }
      const effectiveRow = { ...row, ...overrides };
      const response = await surgeryService.examinationProcess.changeExaminationStatus({
        ptid: effectiveRow.ptid,
        ppid: effectiveRow.ppid,
        psid: 5810,
        upn: effectiveRow.upn,
        status: "Hasta Tetkik için Gönderildi",
      });

      if (response.issuccess) {
        enqueueSnackbar(t("nurse-examination.messages.send-to-examination-success"), {
          variant: "success",
          autoHideDuration: 3000,
        });

        await handleSearch();

        if (
          selectedRow &&
          selectedRow.ptid === effectiveRow.ptid &&
          selectedRow.ppid === effectiveRow.ppid
        ) {
          await fetchExaminationStatus({ ...effectiveRow, id: row.id });
        }
      } else {
        enqueueSnackbar(response.message || t("nurse-examination.messages.send-to-examination-error"), {
          variant: "error",
          autoHideDuration: 5000,
        });
      }
    } catch (error) {
      console.error("Error sending patient to examination:", error);
      enqueueSnackbar(t("nurse-examination.messages.send-to-examination-error"), {
        variant: "error",
        autoHideDuration: 5000,
      });
    }
  }, [t, enqueueSnackbar, handleSearch, selectedRow, fetchExaminationStatus, rowInfoMap]);

  const handleAcceptToService = useCallback(async (row: PatientListRow) => {
    try {
      let overrides: RowInfoOverride | undefined = rowInfoMap[row.id];
      if (!overrides) {
        overrides = (await fetchExaminationStatus(row)) || undefined;
      }
      if (!overrides) {
        enqueueSnackbar(t("nurse-examination.messages.status-error"), {
          variant: "error",
          autoHideDuration: 5000,
        });
        return;
      }
      const effectiveRow = { ...row, ...overrides };
      const response = await surgeryService.examinationProcess.changeExaminationStatus({
        ptid: effectiveRow.ptid,
        ppid: effectiveRow.ppid,
        psid: 5820,
        upn: effectiveRow.upn,
        status: "Hasta Servise Kabul Edildi",
      });

      if (response.issuccess) {
        enqueueSnackbar(t("nurse-examination.messages.accept-to-service-success"), {
          variant: "success",
          autoHideDuration: 3000,
        });

        await handleSearch();

        if (
          selectedRow &&
          selectedRow.ptid === effectiveRow.ptid &&
          selectedRow.ppid === effectiveRow.ppid
        ) {
          await fetchExaminationStatus({ ...effectiveRow, id: row.id });
        }
      } else {
        enqueueSnackbar(response.message || t("nurse-examination.messages.accept-to-service-error"), {
          variant: "error",
          autoHideDuration: 5000,
        });
      }
    } catch (error) {
      console.error("Error accepting patient to service:", error);
      enqueueSnackbar(t("nurse-examination.messages.accept-to-service-error"), {
        variant: "error",
        autoHideDuration: 5000,
      });
    }
  }, [t, enqueueSnackbar, handleSearch, selectedRow, fetchExaminationStatus, rowInfoMap]);

  const patientListColumns: GridColDef[] = useMemo(
    () => [
      {
        field: "__actions__",
        headerName: "",
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const row = params.row as PatientListRow;

          const currentPsid = row.lastpsid ?? row.psid;
          // psid 5805 ise "Tetkik İçin Gönder" aktif olmalı
          const isSendToExaminationEnabled = currentPsid === 5805;
          // psid 5815 ise "Hastayı Servise Kabul Et" aktif olmalı
          const isAcceptToServiceEnabled = currentPsid === 5815;

          return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <ByzMuiOpenProcessMenu
                menuItems={[
                  {
                    label: t("nurse-examination.patient-list.actions.send-to-examination"),
                    onClick: () => {
                      void handleSendToExamination(row);
                    },
                    disabled: !isSendToExaminationEnabled,
                  },
                  {
                    label: t("nurse-examination.patient-list.actions.accept-to-service"),
                    onClick: () => {
                      void handleAcceptToService(row);
                    },
                    disabled: !isAcceptToServiceEnabled,
                  },
                ]}
              />
            </Box>
          );
        },
      },
      {
        field: "patientName",
        headerName: t("nurse-examination.patient-list.columns.patient-name"),
        flex: 1,
        renderCell: (params) => (
          <Box sx={{ fontWeight: "bold" }}>{params.value}</Box>
        ),
      },
      {
        field: "patientSurname",
        headerName: t("nurse-examination.patient-list.columns.patient-surname"),
        flex: 1,
        renderCell: (params) => (
          <Box sx={{ fontWeight: "bold" }}>{params.value}</Box>
        ),
      },
      {
        field: "lastStatus",
        headerName: t("nurse-examination.patient-list.columns.last-status"),
        flex: 1.2,
        renderCell: (params) => {
          const row = params.row as PatientListRow;
          const lastpsid = row.lastpsid ?? row.psid;

          let backgroundColor = "transparent";
          if (lastpsid === 5805) {
            // Hasta Servisten Çağırıldı
            backgroundColor = "#cbe8fa";
          } else if (lastpsid === 5815) {
            // Hasta Tetkik için Gönderildi
            backgroundColor = "#f6e4b6";
          } else if (lastpsid === 5810) {
            // Hasta Servise Gönderildi
            backgroundColor = "#ffd07e";
          } else if (lastpsid === 5820) {
            // Hasta Servise Kabul Edildi
            backgroundColor = "#b9d6ba";
          }

          return (
            <Box
              sx={{
                backgroundColor,
                fontStyle: "italic",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                padding: "0 8px",
              }}
            >
              {params.value}
            </Box>
          );
        },
      },
      {
        field: "fileNo",
        headerName: t("nurse-examination.patient-list.columns.file-no"),
        flex: 1,
      },
      {
        field: "protocolNo",
        headerName: t("nurse-examination.patient-list.columns.protocol-no"),
        flex: 1,
      },
      {
        field: "department",
        headerName: t("nurse-examination.patient-list.columns.department"),
        flex: 1,
      },
      {
        field: "doctorName",
        headerName: t("nurse-examination.patient-list.columns.doctor-name"),
        flex: 1,
      },
      {
        field: "processName",
        headerName: t("nurse-examination.patient-list.columns.process-name"),
        flex: 1,
      },
    ],
    [t, handleSendToExamination, handleAcceptToService]
  );

  const examinationStatusColumns: GridColDef[] = useMemo(
    () => [
      {
        field: "id",
        headerName: t("nurse-examination.examination-status.columns.id"),
        flex: 0.5,
      },
      {
        field: "actionUser",
        headerName: t("nurse-examination.examination-status.columns.action-user"),
        flex: 1,
        renderCell: (params) => (
          <Box sx={{ fontWeight: "bold" }}>{params.value}</Box>
        ),
      },
      {
        field: "actionTime",
        headerName: t("nurse-examination.examination-status.columns.action-time"),
        flex: 1,
        valueFormatter: (value: string) => dateTimeFormatter(value),
      },
      {
        field: "status",
        headerName: t("nurse-examination.examination-status.columns.status"),
        flex: 1.2,
      },
      {
        field: "changedBy",
        headerName: t("nurse-examination.examination-status.columns.changed-by"),
        flex: 1,
      },
      {
        field: "changeTime",
        headerName: t("nurse-examination.examination-status.columns.change-time"),
        flex: 1,
        valueFormatter: (value: string) => dateTimeFormatter(value),
      },
    ],
    [t]
  );

  const examinationStatusSortModel = useMemo<GridSortModel>(
    () => [
      {
        field: "id",
        sort: "desc",
      },
    ],
    []
  );

  // Tarih değiştiğinde otomatik search yap
  useEffect(() => {
    if (selectedDate) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleRowClick = useCallback(async (row: PatientListRow) => {
    setSelectedRowId(row.id);
    const currentPsid = row.lastpsid ?? row.psid;
    const infoOverrides = rowInfoMap[row.id];
    const effectiveRow = infoOverrides ? { ...row, ...infoOverrides } : row;
    const rowWithPsid = currentPsid ? { ...effectiveRow, psid: currentPsid } : effectiveRow;
    setSelectedRow(rowWithPsid);
    await fetchExaminationStatus(rowWithPsid);
  }, [fetchExaminationStatus, rowInfoMap]);

  return (
    <Box sx={{ width: "100%", py: 3 }}>
      <Grid container spacing={2}>
        <Grid xs={12}>
          <Card sx={{ p: 2, mb: 2 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
              sx={{
                pb: 1,
                borderBottom: "1px solid #E0E0E0",
                flexWrap: "nowrap",
                overflow: "hidden",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                gap={1.5}
                sx={{
                  flexWrap: "nowrap",
                  flexShrink: 0,
                  minWidth: 0,
                }}
              >
                <ByzMuiTypography isTitle={true} sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                  {t("nurse-examination.preparation.header.title")}
                </ByzMuiTypography>
                <ByzMuiTypography sx={{ fontStyle: "italic", fontSize: 14, whiteSpace: "nowrap", flexShrink: 0 }}>
                  {t("nurse-examination.preparation.header.dateLabel")}{" "}
                  {format(selectedDate ?? new Date(), "dd.MM.yyyy")}
                </ByzMuiTypography>
                <Box sx={{ flexShrink: 0 }}>
                  <ByzDaySelectButton
                    previousText={t("nurse-examination.preparation.actions.previous-day")}
                    todayText={t("nurse-examination.preparation.actions.today")}
                    nextText={t("nurse-examination.preparation.actions.next-day")}
                    onChange={(direction) =>
                      setSelectedDate((prev) => {
                        const base = prev ?? new Date();
                        if (direction === "previous") return addDays(base, -1);
                        if (direction === "next") return addDays(base, 1);
                        return new Date();
                      })
                    }
                  />
                </Box>
              </Box>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              gap={1.5}
              flexWrap="wrap"
              mt={1}
            >

              <SimpleSelectListPopup
                size="small"
                label={t("nurse-examination.preparation.filters.building")}
                value={building?.NAME || building?.name || ""}
                onChange={handleBuildingChange}
                url={`/reference/api/HospitalBuilding/GetHospitalBuildingsWithPrivileges/${selectedOfficeId || 2}`}
                method="GET"
                dialogTitle={t("nurse-examination.preparation.filters.select-building")}
                displayField="NAME"
              />

              <SimpleSelectListPopup
                size="small"
                label={t("nurse-examination.preparation.filters.floor")}
                value={floor?.NAME || floor?.name || ""}
                onChange={handleFloorChange}
                url={
                  building
                    ? `/reference/api/HospitalFloors/GetAllHospitalFloorsByHbId/${building.ID || building.id}`
                    : ""
                }
                method="GET"
                dialogTitle={t("nurse-examination.preparation.filters.select-floor")}
                displayField="NAME"
                disabled={!building}
                placeholder={
                  building
                    ? ""
                    : t("nurse-examination.preparation.filters.select-building-first")
                }
              />

              <SimpleSelectListPopup
                size="small"
                label={t("nurse-examination.preparation.filters.department")}
                value={department?.NAME || department?.name || ""}
                onChange={(value: any) => setDepartment(value)}
                url={`/reference/api/Department/GetAllDepartmentsByOffId?offId=${selectedOfficeId || 2}`}
                method="GET"
                dialogTitle={t("nurse-examination.preparation.filters.select-department")}
                displayField="NAME"
              />
              <ByzMuiSearchFilterButton width="46px" height="46px" onClick={handleSearch} disabled={isLoading} />

              <Tooltip title="Geliştirme Yapılacak">
                <Box display="flex" alignItems="center" gap={1} sx={{ ml: "auto" }}>
                  <Divider orientation="vertical" flexItem sx={{ borderColor: "black", width: "2px" }} />
                  <ByzMuiPrintButton type={1} disabled={true} />
                  <ByzMuiPrintButton type={2} disabled={true} />
                  <ByzMuiPrintButton type={3} disabled={true} />
                </Box>
              </Tooltip>
            </Box>
          </Card>

          {/* Hasta Listesi */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box
              mb={2}
              sx={{
                pb: 1,
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle>
                {t("nurse-examination.patient-list.title")}
              </ByzMuiTypography>
            </Box>

            {patientListRows.length > 0 ? (
              <ByzMuiDataGrid
                rows={patientListRows}
                columns={patientListColumns}
                pageSize={10}
                disableProcessMenu
                autoHeight
                whiteGrid={true}
                rowSelectionModel={selectedRowId ? [selectedRowId] : []}
                onRowSelectionModelChange={(newSelection) => {
                  const selectedId = Array.isArray(newSelection) && newSelection.length > 0 ? newSelection[0] : null;
                  if (selectedId) {
                    const row = patientListRows.find((r) => r.id === selectedId);
                    if (row) {
                      handleRowClick(row);
                    }
                  }
                }}
                onRowClick={(params) => handleRowClick(params.row as PatientListRow)}
              />
            ) : (
              <ByzMuiTypography color="GrayText">
                {t("nurse-examination.patient-list.empty")}
              </ByzMuiTypography>
            )}
          </Paper>

          {/* Tetkik Durumu */}
          <Paper sx={{ p: 2 }}>
            <Box
              mb={2}
              sx={{
                pb: 1,
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle>
                {t("nurse-examination.examination-status.title")}
              </ByzMuiTypography>
            </Box>

            {examinationStatusRows.length > 0 ? (
              <ByzMuiDataGrid
                rows={examinationStatusRows}
                columns={examinationStatusColumns}
                pageSize={10}
                disableProcessMenu
                autoHeight
                disableRowSelectionOnClick
                loading={isLoadingStatus}
                whiteGrid={true}
                sortModel={examinationStatusSortModel}
                onSortModelChange={() => { }}
              />
            ) : (
              <ByzMuiTypography color="GrayText">
                {t("nurse-examination.examination-status.empty")}
              </ByzMuiTypography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NurseExamination;

